import {useEffect} from "react";

import {compareCoords, getCommonCoords, getFeatureCoords} from "../../lookFunctions";
import {getCurrentLayout} from "../../../../appLayout";
import {featureHasSelectedFilters, s3FilterImagePath} from "../../../../../misc/utils";
import {WIZARD_GROUP_TYPES} from "../../../../../interfaces/wizardItemInterface";
import Misc from "../../../../../misc/misc";
import {getCompressedFeature} from "../../../../../misc/backendFunctions";
import {getBvFeatures} from "../../../../../api/server-api";
import store from "../../../../../store";
import {setWizardMissingFeatures} from "../../../../../actions/wizardFunctions";
import {getFeatureGroupFeaturesQuantity} from "../../lookNavigationFunctions";
import Config, {AVAILABLE_VISUALIZER_TYPES, getCommonVisualizerType, IS_LOOK_JOURNEY} from "../../../../../config";
import {
  getNextWizardSteps,
  getWizardGroupName,
  isSameVisualizerWizard,
  selectedWizardGroupIsNotInCurrentStepAnymore
} from "./lookWizardFunctions2";
import {CABINET_GROUP, COUNTERTOP_GROUP} from "../../../../kvComponent/core/constants";
import {COUNTERTOP_GROUP_BATHROOM, REQUIRE_REGEX_GROUPS, ShowerComboTubType} from "../../../core/constants";
import {getFeatureGroupName} from "../../../../../misc/helpers";
import {getWizardIndex} from "./lookWizardIndex";
import {ShowerTubCombo} from "../../../core/parts/showerTubCombo";
import {hardCodeIsRebath} from "../../../../../misc/fabricatorHardcode";
import {Vanity} from "../../../core/parts/vanity";
import {getRegexReplacedName} from "../../../bathroomFunctions";

const regexForWizardId = /\/wizard\/(.*?)$/;
const regexForLookRoot = /\/look-[^/]*/;

/**
 * returns the wizard item id from the url (/look-0/wizard/190 -> "190")
 * @param pathname {string}
 * @returns {string}
 */
export const getWizardIdFromUrl = (pathname = "") => {
  const match = pathname.match(regexForWizardId);
  if (!Array.isArray(match) || !match[1]) {
    return ""
  }
  return match[1].split("/")[0] // to support the trailing slash urls
}

/**
 * strips everything after the look id (/look-0/wizard/190 -> "/look-0")
 * @param pathname {string}
 * @returns {string}
 */
export const getLookRootUrl = (pathname = "") => {
  const match = pathname.match(regexForLookRoot);
  return Array.isArray(match) && match[0] ? match[0] : "/"
}

/**
 * returns current wizard list for visualizer
 * @param wizardList {[wizardItemInterface]}
 * @param activePreset {string|number}
 * @returns {wizardItemInterface[]}
 */
export const getCurrentWizardList = (wizardList, activePreset) => {
  return wizardList.filter(wizardItem => !wizardIsDisabled(wizardItem, activePreset))
}

/**
 * returns current wizard step section items
 * @param wizardItem {wizardItemInterface}
 * @param activePreset {string|number}
 * @returns {boolean}
 */
export const wizardIsDisabled = (wizardItem, activePreset) => {
  if (!isSameVisualizerWizard(wizardItem)) {
    return true
  }
  return !wizardItem.disabledScenes || (typeof activePreset !== "undefined" && wizardItem.disabledScenes.includes(activePreset))
}

/**
 * checks if we need to show the wizard in the project scope and shoppingcart for the QuotingTool
 * @param wizardList {[wizardItemInterface]}
 * @param wizardIsActive {boolean}
 * @returns {boolean}
 */
export const showWizardInsteadOfQT = (wizardList, wizardIsActive) => {
  return wizardIsActive && wizardList.length
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param location {object}
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getActiveWizardItem = (wizardList, location) => {
  if (!Array.isArray(wizardList) || !location) {
    return null
  }
  const wizardIdFromUrl = getWizardIdFromUrl(location.pathname);
  if (!wizardIdFromUrl) {
    return null
  }
  const stringId = +wizardIdFromUrl;
  return wizardList.find(wizItem => wizItem.id === stringId);
}


/**
 * returns layout's featureGroup
 * @param wizardGroup {wizardGroupInterface}
 * @param layout {object}
 * @returns featureGroup {featureGroupInterface}
 */
export const getFeatureGroupFromWizardGroup = (wizardGroup, layout = getCurrentLayout()) => {
  if (!wizardGroup) {
    return null
  }
  if (layout.visualizerType === AVAILABLE_VISUALIZER_TYPES.Kitchen || layout.visualizerType === AVAILABLE_VISUALIZER_TYPES.kvScene) {
    switch (wizardGroup.groupName) {
      case "Edge Profile":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_edge" : "edge_profile")
      case "Sink":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_sink" : "sink")
      case "Faucet":
        return layout.getFeatureGroupByPartId(wizardGroup.isCenterIslandGroup ? "center_island_faucet" : "faucet")
      default:
        break;
    }
  }
  return layout.getFeatureGroupByGroupName(wizardGroup.groupName)
}

/**
 * returns current wizard step section items
 * @param wizardGroup {wizardGroupInterface}
 * @param featureGroup {featureGroupInterface}
 * @param quoteIndex {number}
 * @param intl {object}
 * @param fabricatorData {object}
 * @returns wizardStepName {string}
 */
export const getWizardStepName = (wizardGroup, featureGroup, quoteIndex, intl, fabricatorData) => {
  let featureGroupName = getWizardGroupName(wizardGroup, featureGroup, quoteIndex);
  if (!featureGroupName && featureGroup) {
    featureGroupName = getFeatureGroupName(featureGroup, intl, fabricatorData)
  }
  return featureGroupName
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param partId {string}
 * @param activePreset {number|string} // check
 * @param firstOnly {boolean} // determines if we need to check only first step featureGroups or all
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const categoryIsInTheWizard = (wizardList, partId = "", activePreset, firstOnly = true) => {
  if (!Array.isArray(wizardList) || !partId) {
    return null
  }
  return wizardList.find(wizItem => {
    if (wizardIsDisabled(wizItem, activePreset)) {
      return false
    }
    let lookWhere = firstOnly ? getCurrentGroupsFromSource("0", wizItem) : wizItem.wizardGroups;
    if (firstOnly && Array.isArray(lookWhere) && lookWhere.length && lookWhere.every(wizGroup => wizGroup.nodeType === "custom")) {
      lookWhere = lookWhere.reduce((total, wizGroup) => {
        return [...total, ...getCurrentGroupsFromSource(wizGroup.id, wizItem)]
      }, []);
    }
    return lookWhere.some(wizardGroup => {
      if (wizardGroup.nodeType !== "group") {
        return false;
      }
      const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
      if (!featureGroup) {
        return false
      }
      return featureGroup.partId === partId
    })
  })
}

/**
 * returns current wizard step section items
 * @param wizardList {[wizardItemInterface]}
 * @param params {object}
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getMaterialsWizardItem = (wizardList, params) => {
  if (!Array.isArray(wizardList) || !params) {
    return null
  }
  return wizardList.find(wizItem => {
    if (!isSameVisualizerWizard(wizItem)) {
      return false
    }
    return (Misc.getUrlFromString(wizItem.name) === params.partName || wizItem.id === +params.partName)
  }) || getDefaultMaterialsWizardItem()
}

/**
 * returns default wizard on switch to page
 * @returns wizardActiveItem {wizardItemInterface}
 */
export const getDefaultMaterialsWizardItem = () => {
  const {quotesState, wizardState} = store.getState();
  const {wizardList, allWizardsValues, wizardActiveItem} = wizardState;
  const {activeQuoteIndex} = quotesState;
  if (!Array.isArray(wizardList)) {
    return null
  }
  const currentVisType = getCommonVisualizerType();
  const lastSelectedWizardInThisQuote = Array.isArray(allWizardsValues) ? allWizardsValues.find(wizItem => {
    return wizItem.quoteIndex === activeQuoteIndex && wizItem.isLastActiveWizard
      && isSameVisualizerWizard(wizItem, currentVisType)
  }) : null;
  if (lastSelectedWizardInThisQuote) {
    const foundWizItem = wizardList.find(wizItem => wizItem.id === lastSelectedWizardInThisQuote.wizardItemId);
    if (foundWizItem) {
      return foundWizItem
    }
  }
  if (wizardActiveItem && isSameVisualizerWizard(wizardActiveItem, currentVisType)) {
    return wizardActiveItem
  }
  return wizardList.find(wizItem => isSameVisualizerWizard(wizItem, currentVisType)) || wizardList[0]
}

let cachedSiblingFeatures = []; // {wizardId: number, groups: [{}, {}], totalFeatures: [] }

/**
 * returns current wizard step section items
 * @param source {string}
 * @param wizardActiveItem {wizardItemInterface}
 * @param sourceSelectedValue {wizardSelectedValueInterface}
 * @returns array {[wizardGroupInterface]}
 */
export const getCurrentGroupsFromSource = (source, wizardActiveItem, sourceSelectedValue = null) => {
  if (!wizardActiveItem) return [];

  const idx = getWizardIndex(wizardActiveItem);
  if (!idx) return [];

  const sourceKey = String(source);
  const targets = idx.edgesBySource.get(sourceKey) || new Set();

  const siblingGroups = [];
  for (const wizGroup of idx.groups) {
    // Direct edge: source -> target
    if (targets.has(wizGroup.id)) {
      siblingGroups.push(wizGroup);
      continue;
    }

    // “Sibling parent” logic using merged groups
    const merged = sourceSelectedValue?.wizardGroup?.mergedGroups;
    const selectedFeature = sourceSelectedValue?.selectedFeature;

    if (merged && selectedFeature) {
      let ok = false;
      for (const wGroup of merged) {
        if (navItemIsExcluded({ part: selectedFeature }, wGroup)) continue;

        const t = idx.edgesBySource.get(String(wGroup.id));
        if (t && t.has(wizGroup.id)) {
          ok = true;
          break;
        }
      }
      if (ok) siblingGroups.push(wizGroup);
    }
  }

  const areSameSiblingGroups =
    siblingGroups.length > 1 &&
    siblingGroups.every(sg => sg.nodeType === WIZARD_GROUP_TYPES.group && sg.groupName === siblingGroups[0].groupName);

  if (!areSameSiblingGroups) return siblingGroups;

  if (siblingGroups.every(g => g.useIncluded && g.featuresIncluded)) {
    let featuresIncluded = [];
    const alreadyCached = cachedSiblingFeatures.find(cacheObject => {
      return (
        cacheObject.wizardId === wizardActiveItem.id &&
        cacheObject.groups.every((wGroup, i) => siblingGroups[i] && wGroup.id === siblingGroups[i].id)
      );
    });

    if (alreadyCached) {
      featuresIncluded = alreadyCached.totalFeatures;
    } else {
      featuresIncluded = siblingGroups.reduce((total, wizardGroup) => {
        wizardGroup.featuresIncluded.forEach(included => {
          if (!total.some(exclObj => exclObj.featureDefinitionId === included.featureDefinitionId)) {
            total.push(included);
          }
        });
        return total;
      }, []);
      cachedSiblingFeatures.push({ wizardId: wizardActiveItem.id, groups: siblingGroups, totalFeatures: featuresIncluded });
    }

    return [
      Object.assign({}, siblingGroups[0], {
        mergedGroups: siblingGroups,
        featuresExcluded: [],
        featuresIncluded,
      }),
    ];
  }

  const allFeaturesExcluded = siblingGroups.reduce((total, wizardGroup) => {
    wizardGroup.featuresExcluded.forEach(excluded => {
      if (!excluded) return;
      if (typeof excluded === "object") {
        if (!total.some(e => typeof e === "object" && e.featureDefinitionId === excluded.featureDefinitionId)) {
          total.push(excluded);
        }
      } else if (!total.includes(excluded)) {
        total.push(excluded);
      }
    });
    return total;
  }, []);

  const featuresExcluded = allFeaturesExcluded.filter(exclFeature => {
    return siblingGroups.every(siblingGroup => {
      if (typeof exclFeature === "object") {
        return siblingGroup.featuresExcluded.some(exclObj => {
          return exclObj && typeof exclObj === "object" && exclObj.featureDefinitionId === exclFeature.featureDefinitionId;
        });
      }
      return siblingGroup.featuresExcluded.includes(exclFeature);
    });
  });

  return [
    Object.assign({}, siblingGroups[0], {
      mergedGroups: siblingGroups,
      featuresExcluded,
    }),
  ];
};


export const getCorrectedWizardGroupId = (stepGroups, stepSelectedValue) => {
  if ((stepGroups.length === 1) && stepGroups[0].mergedGroups && stepSelectedValue && stepSelectedValue.wizardGroup
    && (stepSelectedValue.wizardGroup.groupName === stepGroups[0].groupName)
    && stepGroups[0].mergedGroups.some(wizG => wizG.id === stepSelectedValue.wizardGroup.id)) {
    const correctedWizG = Object.assign({}, stepGroups[0], {
      id: stepSelectedValue.wizardGroup.id
    });
    return [correctedWizG]
  }
  return stepGroups
}

/**
 * this changes cart items wizard groups to use new wizard group ids
 * @param wizardGroup {wizardGroupInterface}
 * @param feature {featureInterface}
 * @returns {wizardGroupInterface|null}
 */
export const wizardGroupHasFeatureIncluded = (wizardGroup, feature) => {
  if (!feature || !wizardGroup) {
    return null
  }
  if (wizardGroup.mergedGroups) {
    const realWizardGroup = wizardGroup.mergedGroups.find(wizG => !navItemIsExcluded({part: feature}, wizG));
    if (realWizardGroup) {
      return Object.assign({}, wizardGroup, {
        id: realWizardGroup.id
      })
    }
    return null
  }
  const isNavItemIncluded = !navItemIsExcluded({part: feature}, wizardGroup)
  return isNavItemIncluded ? wizardGroup : null
}

/**
 * returns fixed wizard group for merged groups
 * @param wizardGroup {wizardGroupInterface}
 * @param feature {featureInterface}
 * @returns {wizardGroupInterface}
 */
export const getCorrectedWizardGroupForFeature = (wizardGroup, feature) => {
  if (wizardGroup.mergedGroups && feature) {
    const realWizardGroup = wizardGroup.mergedGroups.find(wizG => !navItemIsExcluded({part: feature}, wizG));
    if (realWizardGroup && (realWizardGroup.id !== wizardGroup.id)) {
      return Object.assign({}, wizardGroup, {
        id: realWizardGroup.id
      })
    }
  }
  return wizardGroup
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardActiveStep {number}
 * @param wizardSelectedValues {object}
 * @returns {wizardGroupInterface[]}
 */
export const getCurrentGroupsWithDoubleCheck = (wizardActiveItem, wizardActiveStep, wizardSelectedValues) => {
  let currentGroups = getCurrentGroups(wizardActiveItem, wizardActiveStep, wizardSelectedValues);
  if (!currentGroups.length) { // added additional check
    const nextWizardSteps = getNextWizardSteps(wizardActiveItem, wizardActiveStep, wizardSelectedValues);
    const stepsObject = nextWizardSteps.find(step => step.stepIndex === wizardActiveStep);
    if (stepsObject) {
      currentGroups = stepsObject.stepGroups;
    }
  }
  return currentGroups;
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardActiveStep {number}
 * @param wizardSelectedValues {object}
 * @param prevStepGroups {[wizardGroupInterface]}
 * @param additionalWizardValuesToCheck {object}
 * @returns {wizardGroupInterface[]}
 */
export const getCurrentGroups = (wizardActiveItem,
                                 wizardActiveStep,
                                 wizardSelectedValues,
                                 prevStepGroups = [],
                                 additionalWizardValuesToCheck= null) => {
  if (!wizardActiveItem || wizardActiveStep < 0) {
    return []
  }
  const stepStr = "" + wizardActiveStep;

  const additionalWizardValue = additionalWizardValuesToCheck && typeof additionalWizardValuesToCheck === 'object'
    ? additionalWizardValuesToCheck[wizardActiveStep]
    : null;

  const toCheckValue = (wizardSelectedValues && wizardSelectedValues[wizardActiveStep]) ?? additionalWizardValue;

  if (wizardActiveStep === 0) {
    return getCorrectedWizardGroupId(
      getCurrentGroupsFromSource(stepStr, wizardActiveItem),
      toCheckValue
    )
  }

  const selectedPrevStep = wizardSelectedValues[wizardActiveStep - 1];
  if (!selectedPrevStep || !selectedPrevStep.wizardGroup
    || (prevStepGroups.length && selectedWizardGroupIsNotInCurrentStepAnymore(selectedPrevStep, prevStepGroups))) {
    if (prevStepGroups.length && prevStepGroups[0].id) {
      if (additionalWizardValuesToCheck) { // here we check also previously selected values to make steps similar to prev choices
        let foundPreviouslySelectedGroup = null;
        if (prevStepGroups.every(wg => wg.nodeType === "custom")) {
          Object.keys(additionalWizardValuesToCheck).forEach(key => {
            if ((key >= Object.keys(wizardSelectedValues).length)
              && !additionalWizardValuesToCheck[key].partId
              && additionalWizardValuesToCheck[key].wizardGroup) {
              prevStepGroups.forEach(wg => {
                if (wg.id === additionalWizardValuesToCheck[key].wizardGroup.id) {
                  foundPreviouslySelectedGroup = wg;
                }
              });
            }
          });
        } else {
          // here we can add simple feature group, but no need for now
        }
        if (foundPreviouslySelectedGroup) {
          return getCorrectedWizardGroupId(
            getCurrentGroupsFromSource(foundPreviouslySelectedGroup.id, wizardActiveItem, selectedPrevStep),
            toCheckValue
          )
        }
      }
      const suggestedStepGroups = getCurrentGroupsFromSource(prevStepGroups[0].id, wizardActiveItem, selectedPrevStep);
      if ((suggestedStepGroups.length === 1 && (suggestedStepGroups[0].nodeType === "group") && suggestedStepGroups[0].mergedGroups && suggestedStepGroups[0].mergedGroups.length)
        && additionalWizardValuesToCheck && Object.keys(additionalWizardValuesToCheck).length) {
        // here we check if there are selections in the old flow that might actually help to select current product
        if (toCheckValue && toCheckValue.wizardGroup
          && (toCheckValue.wizardGroup.groupName === suggestedStepGroups[0].groupName)
          && toCheckValue.selectedFeature
          && suggestedStepGroups[0].mergedGroups.some(wizG => !navItemIsExcluded({part: toCheckValue.selectedFeature}, wizG))) {
          return [getCorrectedWizardGroupForFeature(
            suggestedStepGroups[0],
            toCheckValue.selectedFeature
          )]
        } else if (!toCheckValue || !toCheckValue.wizardGroup || (toCheckValue.wizardGroup.groupName !== suggestedStepGroups[0].groupName)) {
          // here we check if there are selections in the old flow that might actually help to select current product
          for (let key = Object.keys(wizardSelectedValues).length + 1; key < Object.keys(additionalWizardValuesToCheck).length; key++) {
            if (key && (key > Object.keys(wizardSelectedValues).length)
              && additionalWizardValuesToCheck[key]
              && additionalWizardValuesToCheck[key].wizardGroup
              && additionalWizardValuesToCheck[key].selectedFeature
              && (additionalWizardValuesToCheck[key].wizardGroup.groupName === suggestedStepGroups[0].groupName)
              && suggestedStepGroups[0].mergedGroups.some(wizG => !navItemIsExcluded({part: additionalWizardValuesToCheck[key].selectedFeature}, wizG))) {
              return [getCorrectedWizardGroupForFeature(
                suggestedStepGroups[0],
                additionalWizardValuesToCheck[key].selectedFeature
              )]
            }
          }
        }
      }
      return getCorrectedWizardGroupId(
        suggestedStepGroups,
        toCheckValue
      )
    }
    // console.log("Previous step is not selected! Check selected values --> ", wizardSelectedValues);
    return []
  }

  if (selectedPrevStep && selectedPrevStep.wizardGroup && selectedPrevStep.wizardGroup.mergedGroups && selectedPrevStep.selectedFeature) {
    const wizardGroupWithSelectedFeature = selectedPrevStep.wizardGroup.mergedGroups.find(wizGroup => {
      return !navItemIsExcluded({part: selectedPrevStep.selectedFeature}, wizGroup)
    });
    if (wizardGroupWithSelectedFeature) {
      return getCorrectedWizardGroupId(
        getCurrentGroupsFromSource(wizardGroupWithSelectedFeature.id, wizardActiveItem, selectedPrevStep),
        toCheckValue
      )
    }
  }

  // const prevStepStr = "" + (wizardActiveStep - 1);
  // const prevGroups = getCurrentGroupsFromSource(prevStepStr, wizardActiveItem, selectedPrevStep);
  // if (prevGroups.length > 1 && wizardActiveStep > 0) {
  //   return [selectedPrevStep.wizardGroup]
  // }

  return getCorrectedWizardGroupId(
    getCurrentGroupsFromSource(selectedPrevStep.wizardGroup.id, wizardActiveItem, selectedPrevStep),
    toCheckValue
  )
}

/**
 * returns current wizard step section items
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 * @param returnAllSteps {boolean}
 * @param additionalWizardValuesToCheck {object}
 * @returns {array}
 */
export const getAvailableSteps = (wizardActiveItem, wizardSelectedValues, returnAllSteps, additionalWizardValuesToCheck = null) => {
  if (!wizardActiveItem?.wizardGroups || !wizardActiveItem?.wizardEdges) return [];

  const maxWizardSteps = 50;
  const firstSelectedValue = wizardSelectedValues?.[0] ?? {};

  const stepsArray = [{
    stepIndex: 0,
    stepGroups: getCorrectedWizardGroupId(getCurrentGroupsFromSource("0", wizardActiveItem), firstSelectedValue),
  }];

  for (let i = 1; i <= maxWizardSteps; i++) {
    const prevStepGroups = returnAllSteps ? (stepsArray[i - 1]?.stepGroups ?? []) : [];
    const currentGroups = getCurrentGroups(wizardActiveItem, i, wizardSelectedValues, prevStepGroups, additionalWizardValuesToCheck);
    if (!currentGroups.length) break;
    stepsArray.push({ stepIndex: i, stepGroups: currentGroups });
  }

  return stepsArray
}

/**
 * returns current wizard step section items
 * @param currentGroups {[wizardGroupInterface]}
 * @returns {array}
 */
export const getWizardStepCommonCoords = (currentGroups) => {
  if (!currentGroups.length) {
    return []
  }
  let commonCoords = {};
  for (let i = 0; i < currentGroups.length; i++) {
    const wizardGroup = currentGroups[i];
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (!featureGroup) {
      continue
    }
    const currentCoords = featureGroup.activeFeature ? getFeatureCoords(featureGroup.activeFeature) : getCommonCoords(featureGroup);
    commonCoords = compareCoords(currentCoords, currentCoords);
  }

  return commonCoords
}

/**
 * returns closest wizard group with nodeType === group
 * @param wizardGroup {wizardGroupInterface}
 * @param wizardActiveItem {wizardItemInterface}
 * @returns {array}
 */
export const findParentWizardFeatureGroup = (wizardGroup, wizardActiveItem) => {
  if (!wizardGroup || !wizardActiveItem) {
    return null
  }
  let wizardFeatureParentGroup = null;

  let maxStack = 5;
  let currentTry = 0;
  const recursiveFindParent = (checkWizardGroup) => {
    const sourceEdge = wizardActiveItem.wizardEdges.find(wizEdge => wizEdge.target === checkWizardGroup.id);
    const nextToCheckWizardGroup = wizardActiveItem.wizardGroups.find(wizGroup => wizGroup.id === sourceEdge.source);
    if (nextToCheckWizardGroup && nextToCheckWizardGroup.nodeType === "group") {
      wizardFeatureParentGroup = nextToCheckWizardGroup;
    } else if (nextToCheckWizardGroup && currentTry < maxStack) {
      currentTry++;
      recursiveFindParent(nextToCheckWizardGroup)
    }
  }
  recursiveFindParent(wizardGroup)

  return wizardFeatureParentGroup
}
/**
 * returns closest wizard group with nodeType === group
 * @param featureGroup {featureGroupInterface}
 * @param selectedFilters {array}
 * @returns {featureInterface}
 */
export const findFeatureBySelectedFilters = (featureGroup, selectedFilters) => {
  if (!featureGroup || !selectedFilters) {
    return null
  }
  if (featureGroup.activeFeature && featureHasSelectedFilters(featureGroup.activeFeature, selectedFilters)) {
    return featureGroup.activeFeature
  }
  const layout = getCurrentLayout();
  const navItems = layout.designNavigationItems({part: featureGroup}, selectedFilters);
  if (!navItems.length) {
    return null
  }
  if (navItems.length > 1 && featureGroup.activeFeature && featureGroup.activeFeature.dbFeature.sharedFeatureName.includes(".")) {
    const skuColor = "." + featureGroup.activeFeature.dbFeature.sharedFeatureName.split(".")[1];
    const sameColor = navItems.find(navItem => navItem.part.dbFeature.sharedFeatureName.includes(skuColor));
    if (sameColor) {
      return sameColor.part
    }
  }
  return navItems[0].part
}

/**
 * @param currentFilterValue {object} filterValueInterface
 * @param filterGroup {object} filterGroupInterface
 * @returns {object}
 */
export const getFilterValueThumbnailAndStyles = (currentFilterValue, filterGroup) => {

  const getFilterItemThumbnail = () => {
    const additionalImages = currentFilterValue.additionalImages;
    if (Array.isArray(additionalImages) && additionalImages.length) {
      return additionalImages[0].imageUrl
    }
    return s3FilterImagePath(currentFilterValue.icon);
  }

  let thumbnail = getFilterItemThumbnail();
  const isColor = (filterGroup.name || "").toLowerCase().includes("color");

  const styles = {
    backgroundSize: isColor  ? "cover" : "contain",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  };
  if (thumbnail) {
    styles.backgroundImage = "url(" + thumbnail + ")";
  }
  if (!thumbnail && currentFilterValue && isColor) {
    styles.backgroundColor = currentFilterValue.value;
  }

  return {thumbnail, styles}
}

// global fabricator setting that disables SKU matching for all wizard groups
const getGlobalDontMatchBySku = () => !!store.getState().fabricatorState.wizardSettings?.dontMatchBySku;

/**
 * @param wizardGroup {wizardGroupInterface}
 * @param navItem {navItemInterface}
 * @param globalDontMatchBySku {boolean} fabricatorState.wizardSettings.dontMatchBySku, read once by the caller
 * @returns {boolean}
 */
const wizardGroupHasIncludedNavItem = (wizardGroup, navItem, globalDontMatchBySku = getGlobalDontMatchBySku()) => {
  if (!navItem || !navItem.part) {
    return false;
  }
  const feature = navItem.part;
  const {sku, featureDefinitionID, customDisplayName} = feature.dbFeature;
  const lowerCasedName = feature.getLowerCasedSharedFeatureName
    ? feature.getLowerCasedSharedFeatureName()
    : (feature.dbFeature.sharedFeatureName || feature.partName || '')
      .trim().toLowerCase().replace('room - ', '');

  const splittedFeatureName = feature.getSplittedFeatureName?.();
  const needsRegexCheck = REQUIRE_REGEX_GROUPS.includes(feature.partId);
  // SKU matching can be disabled per wizard group or globally via the fabricator "wizardSettings"
  const matchBySku = !globalDontMatchBySku && !wizardGroup.dontMatchFeaturesBySku;

  return wizardGroup.featuresIncluded.some(includedFeature => {
    if (
      (includedFeature.sharedFeatureName && includedFeature.sharedFeatureName === lowerCasedName)
      || includedFeature.featureDefinitionId === featureDefinitionID
      || (!includedFeature.sharedFeatureName && includedFeature.name === customDisplayName)
      || (matchBySku && includedFeature.sku && sku && includedFeature.sku === sku)
    ) {
      return true;
    }
    return needsRegexCheck
      && !!includedFeature.sharedFeatureName
      && getRegexReplacedName(includedFeature.sharedFeatureName, includedFeature) === splittedFeatureName;
  });
}

/**
 * @param navItem {navItemInterface}
 * @param wizardGroup {wizardGroupInterface}
 * @param globalDontMatchBySku {boolean} optional, pass it when calling in a loop to avoid re-reading the store
 * @returns {boolean}
 */
export const navItemIsExcluded = (navItem, wizardGroup, globalDontMatchBySku) => {
  if (wizardGroup.useIncluded && wizardGroup.featuresIncluded) {
    const dontMatchBySku = globalDontMatchBySku ?? getGlobalDontMatchBySku();
    let mainFeatureExcluded = !wizardGroupHasIncludedNavItem(wizardGroup, navItem, dontMatchBySku);
    if (IS_LOOK_JOURNEY && mainFeatureExcluded && navItem.colorOptions?.length) {
      return !navItem.colorOptions.some(colorOption => {
        return wizardGroupHasIncludedNavItem(wizardGroup, {part: colorOption.feature}, dontMatchBySku);
      });
    }
    return mainFeatureExcluded;
  }
  const {sku, featureDefinitionID, customDisplayName} = navItem.part.dbFeature;
  const {partName} = navItem.part; // it replaces the {groupName + "-"} from the name, same logic as we have in the dashboard
  // if (customDisplayName === "Only Front Edge") {
  //   console.log("checking excluded from wizardGroup", wizardGroup);
  //   debugger;//eslint-disable-line
  // }
  const lowerCasedName = navItem.part.getLowerCasedSharedFeatureName
    ? navItem.part.getLowerCasedSharedFeatureName()
    : (navItem.part.dbFeature.sharedFeatureName || partName || '')
      .trim().toLowerCase().replace('room - ', '');
  for (let excluded of wizardGroup.featuresExcluded) {
    if (excluded && (typeof excluded === "object")) {
      if (
        (excluded.sharedFeatureName && (excluded.sharedFeatureName === lowerCasedName))
        || (excluded.featureDefinitionId === featureDefinitionID)
        || (excluded.name === customDisplayName)
        || (excluded.sku && sku && (excluded.sku === sku))
      ) {
        return true
      }
      // if (excluded.name === customDisplayName || excluded.name === sharedFeatureName || (excluded.sku && sku && (excluded.sku === sku))) {
    } else if (excluded) {
      // if (excluded === sku || excluded === sharedFeatureName || excluded === partName || excluded === featureDefinitionID) {
      if (excluded === sku || excluded === partName || excluded === featureDefinitionID) {
        return true
      }
    }
  }
  return false
}

/**
 * @param navItems {[navItemInterface]}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {[navItemInterface]}
 */
export const filterWizardNavItems = (navItems, wizardGroup) => {
  if (!wizardGroup.useIncluded && (!Array.isArray(wizardGroup.featuresExcluded) || !wizardGroup.featuresExcluded.length)) {
    return navItems
  }
  // console.log("navItems to filter", navItems);
  const globalDontMatchBySku = getGlobalDontMatchBySku(); // read once for the whole list
  if (!IS_LOOK_JOURNEY) {
    return navItems.filter(navItem => !navItemIsExcluded(navItem, wizardGroup, globalDontMatchBySku));
  }

  const addedFeatureIds = new Set();

  return navItems.reduce((acc, navItem) => {
    const mainIncluded = wizardGroupHasIncludedNavItem(wizardGroup, navItem, globalDontMatchBySku);

    // pick the "effective" feature we want to show (main part OR a matching color option)
    let effectiveFeature = navItem.part;
    let matchedColorOption = null;

    if (!mainIncluded) {
      matchedColorOption = navItem.colorOptions?.find(({ feature }) =>
        wizardGroupHasIncludedNavItem(wizardGroup, {part: feature}, globalDontMatchBySku)
      );

      if (!matchedColorOption) {
        return acc; // exclude
      }

      effectiveFeature = matchedColorOption.feature;
    }

    const effectiveId = effectiveFeature?.dbFeature?.id;
    if (effectiveId != null && addedFeatureIds.has(effectiveId)) {
      return acc; // prevent duplicates
    }
    if (effectiveId != null) {
      addedFeatureIds.add(effectiveId);
    }

    acc.push({
      ...navItem,
      part: effectiveFeature,
      colorOptions: (navItem.colorOptions ?? []).filter((opt) => {
        // remove the one we used as the replacement
        if (matchedColorOption && opt.feature?.dbFeature?.id === effectiveId) return false;
        return wizardGroupHasIncludedNavItem(wizardGroup, {part: opt.feature}, globalDontMatchBySku);
      }),
      // selectedColorOption: matchedColorOption,
      // replacedByColorOption: !!matchedColorOption,
    });

    return acc;
  }, []);
}

/**
 * @param wizardSelectedValues {object} // selected wizard steps values
 * @param wizardActiveStep {number} wizardActiveStep
 * @param wizardActiveItem {wizardItemInterface} wizardItemInterface
 * @param wizardGroup {wizardGroupInterface} wizardGroupInterface
 * @param featureGroup {featureGroupInterface} featureGroupInterface
 * @param filterGroups {[filterGroupInterface]}
 * @param searchText {string}
 * @param filters {object}
 * @param shopPageOpened {boolean}
 * @param showPrevStepsColorFirst {boolean}
 * @returns {array}
 */
export const getCurrentWizardNavItems = (wizardSelectedValues,
                                         wizardActiveStep,
                                         wizardActiveItem,
                                         wizardGroup,
                                         featureGroup = null,
                                         filterGroups = [],
                                         searchText = "",
                                         filters = [],
                                         shopPageOpened = false,
                                         showPrevStepsColorFirst = false) => {

  const layout = getCurrentLayout();
  featureGroup = featureGroup || getFeatureGroupFromWizardGroup(wizardGroup);
  if (!featureGroup) {
    return []
  }

  const selectedStepsReversed = Object.keys(wizardSelectedValues).reverse() // is needed to take last selected steps first

  selectedStepsReversed.forEach(selectedStep => {
      const {selectedFilter} = wizardSelectedValues[selectedStep];
      if (!selectedFilter || +selectedStep >= wizardActiveStep) {
        return
      }
      const parentWizardGroup = findParentWizardFeatureGroup(wizardSelectedValues[selectedStep].wizardGroup, wizardActiveItem);
      if (!parentWizardGroup) {
        return null
      }
      const parentFeatureGroup = getFeatureGroupFromWizardGroup(parentWizardGroup);
      if (parentFeatureGroup && parentFeatureGroup.partId === featureGroup.partId) {
        filters.push({
          filterId: selectedFilter.id,
          filterGroupId: selectedFilter.filterGroup_id,
        })
      }
    });

  let navItems = layout.designNavigationItems(
    {part: featureGroup},
    filters,
    searchText,
    featureGroup.activeFeature,
    "all",
    filterGroups,
    {returnAllFeatures: shopPageOpened, wizardGroup, wizardActiveItem}
  );

  navItems = filterWizardNavItems(navItems, wizardGroup);

  if (featureGroup.partId === "vanity_sink") {
    const selectedPrevStep = wizardSelectedValues[wizardActiveStep - 1];
    if (selectedPrevStep  && selectedPrevStep.partId === "vanity_faucet" && selectedPrevStep.selectedFeature) {
      const faucetParent = selectedPrevStep.selectedFeature.getParent();
      if (selectedPrevStep.selectedFeature.matchesMetaForArrayOfConditions(faucetParent.getArrayOfConditions())) {
        const selectedFaucetConditions = faucetParent.getActiveFeatureConditions(selectedPrevStep.selectedFeature)
        const filteredNavItems = navItems.filter(navItem => {
          return selectedFaucetConditions.every(condition => navItem.part.matchesMetaForCondition(condition.propName, condition.propValue))
        });
        // console.log("navItems left", filteredNavItems);
        return filteredNavItems
      }
    }
  }

  if (showPrevStepsColorFirst && !wizardSelectedValues[wizardActiveStep]) {
    const allSelectedColors = selectedStepsReversed.reduce((total, selectedStep) => {
      if (wizardSelectedValues[selectedStep] && wizardSelectedValues[selectedStep].selectedFeature) {
        const colorName = wizardSelectedValues[selectedStep].selectedFeature.getColorName(filterGroups);
        if (colorName && !total.includes(colorName)) {
          total.push(colorName)
        }
      }
      return total
    }, []);
    // console.log("navItems222", navItems);
    // console.log("allSelectedColors", allSelectedColors);
    // console.log("wizardGroup", wizardGroup);
    if (allSelectedColors.length) {
      navItems = navItems.map(navItem => {
        if (navItem.colorOptions && navItem.colorOptions.length) {
          const currentColorOption = navItem.part.getColorName(filterGroups);
          if (currentColorOption !== allSelectedColors[0]) {
            let optionMatchForPrevSelectedColor = null;
            allSelectedColors.forEach(selectedColor => {
              if (!optionMatchForPrevSelectedColor && (currentColorOption !== selectedColor)) {
                optionMatchForPrevSelectedColor = navItem.colorOptions.find(option => {
                  return option.feature.getColorName(filterGroups) === selectedColor
                })
              }
            })
            if (optionMatchForPrevSelectedColor) {
              // console.log("replaced! from", navItem);
              // console.log("replaced! to", {part: optionMatchForPrevSelectedColor});
              return {part: optionMatchForPrevSelectedColor.feature}
            }
          }
        }
        return navItem
      })
    }
  }


  return navItems
}

const searchForNeededWizard = (partIdGroups, wizardList, activePreset) => {
  const wizardWithFirstGroupThatMatches = wizardList.find(wizardItem => {
    if (wizardIsDisabled(wizardItem, activePreset)) {
      return false
    }
    const firstGroup = getCurrentGroupsFromSource("0", wizardItem)[0];
    if (!firstGroup || firstGroup.nodeType !== "group") {
      return false
    }
    const featureGroup = getFeatureGroupFromWizardGroup(firstGroup);
    if (!featureGroup || !featureGroup.partId) {
      return false
    }
    return partIdGroups.includes(featureGroup.partId);
  });
  // console.log("wizardWithFirstGroupThatMatches", wizardWithFirstGroupThatMatches);
  return wizardWithFirstGroupThatMatches || wizardList.find(wizardItem => {
    if (wizardIsDisabled(wizardItem, activePreset)) {
      return false
    }
    // const currentGroups = getCurrentGroups(wizardItem, 0, {});
    // // console.log("currentGroups", currentGroups);
    // if (!currentGroups || !currentGroups.length) {
    //   return false
    // }
    return wizardItem.wizardGroups.some(wizardGroup => {
      const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup)
      return featureGroup && partIdGroups.includes(featureGroup.partId)
    })
  });
}

/**
 * is used for context menu, to detect vanity wizard or shower wizard
 * @param partIdGroups {[string]}
 * @param wizardList {[wizardItemInterface]}
 * @param activePreset {string|number}
 * @returns {wizardItemInterface}
 */
export const getExistingWizardForGroups = (partIdGroups, wizardList, activePreset) => {

  if ((getCommonVisualizerType() === "bathroom")
    && wizardList.some(wizItem => Array.isArray(wizItem.openFromContextForGroups)
    && wizItem.openFromContextForGroups.length)) {
    const layout = getCurrentLayout();
    const currentLayers = layout.getLayers();
    const wizardMatchesSelectedOpenFromContext = wizardList.find(wizItem => {
      if (!Array.isArray(wizItem.openFromContextForGroups)) return false;
      return wizItem.openFromContextForGroups.some(openFromContextForGroup => {
        if (!openFromContextForGroup?.groupName) return false; // just in case
        if (openFromContextForGroup.groupName === "Pedestal Sink") {
          const vanityParent = layout.parts[Vanity.id];
          return vanityParent && !vanityParent.vanity_type
        }
        const featureGroup = layout.getFeatureGroupByGroupName(openFromContextForGroup.groupName);
        if (!featureGroup) return false;
        return partIdGroups.some(partId => partId === featureGroup.partId)
          && currentLayers.some(layer => layer.partId === featureGroup.partId)
      })
    });
    if (wizardMatchesSelectedOpenFromContext) {
      // console.log("wizardMatchesSelectedOpenFromContext", wizardMatchesSelectedOpenFromContext);
      return wizardMatchesSelectedOpenFromContext
    }
  }
  if (partIdGroups[0] === "shower_fixture" && hardCodeIsRebath()) {
    const layout = getCurrentLayout();
    const showerComboParent = layout.parts[ShowerTubCombo.id];
    const toSearchWizardList = wizardList.filter(wizardItem => {
      const firstGroup = getCurrentGroupsFromSource("0", wizardItem)[0];
      if (!firstGroup) {
        return false
      }
      if (showerComboParent.showerTubComboIsActive) {
        if (showerComboParent.bottomTub === ShowerComboTubType.replacement_tub) {
          return firstGroup.groupName === "Replacement Tub"
        } else if (showerComboParent.bottomTub === ShowerComboTubType.walk_in_tub) {
          return firstGroup.groupName === "Walk In Tub"
        }
      } else {
        return firstGroup.groupName !== "Replacement Tub" && firstGroup.groupName !== "Walk In Tub"
      }
    });
    return searchForNeededWizard(partIdGroups, toSearchWizardList, activePreset)
      || searchForNeededWizard(partIdGroups, wizardList, activePreset)
  }
  return searchForNeededWizard(partIdGroups, wizardList, activePreset)
}

/**
 * is used for removing the layers from the canvas which are not selected in the wizard
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 * @param layers {[]}
 * @returns partIdGroups {[string]}
 */
export const getFilteredLayersForActiveWizard = (wizardActiveItem, wizardSelectedValues, layers) => {
  if (!wizardActiveItem || !wizardActiveItem.hideItems) {
    return layers
  }
  wizardActiveItem.wizardGroups.forEach(wizardGroup => {
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (featureGroup && !Object.keys(wizardSelectedValues).some(stepIndex => {
      return wizardSelectedValues[stepIndex].partId === featureGroup.partId && wizardSelectedValues[stepIndex].selectedFeature
    })) {
      layers = layers.filter(layer => layer.partId !== featureGroup.partId) // if there is a step and it is not selected - remove it
    }
  })
  return layers
}

/**
 * is used for removing the feature groups to prevent them appear after the wizard is finished
 * @param wizardActiveItem {wizardItemInterface}
 * @param wizardSelectedValues {object}
 */
export const disableNotSelectedFeatureGroups = (wizardActiveItem, wizardSelectedValues) => {
  wizardActiveItem.wizardGroups.forEach(wizardGroup => {
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (featureGroup && !Object.keys(wizardSelectedValues).some(stepIndex => {
      return wizardSelectedValues[stepIndex].partId === featureGroup.partId && wizardSelectedValues[stepIndex].selectedFeature
    })) {
      featureGroup.disabled = true;
      if (featureGroup.layerPositions) { // we need it here to prevent adding to the cart through restore cart on item change
        Object.keys(featureGroup.layerPositions).forEach(position => {
          featureGroup.layerPositions[position] = null;
        })
      }
      if (featureGroup.disabledClick) {
        featureGroup.disabledClick()
      }
    }
  })
}

/**
 * is used to compress the data that will be passed in the job data back and force
 * @param allWizardsValues {array}
 * @param allWizardsValues {array}
 */
export const getCompressedAllWizardValues = (allWizardsValues) => {
  const allWizardsValuesToSave = allWizardsValues.map(newWizObject => {
    let wizValues = {};
    Object.keys(newWizObject.wizardSelectedValues).forEach(stepIndex => {
      wizValues[stepIndex] = Object.assign({}, newWizObject.wizardSelectedValues[stepIndex])
      if (newWizObject.wizardSelectedValues[stepIndex].wizardGroup) {
        wizValues[stepIndex].wizardGroup = Object.assign(
          {},
          newWizObject.wizardSelectedValues[stepIndex].wizardGroup,
          {featuresExcluded: [], featuresIncluded: []} // to keep clean in json
        )
        if (wizValues[stepIndex].wizardGroup.mergedGroups) {
          wizValues[stepIndex].wizardGroup.mergedGroups = wizValues[stepIndex].wizardGroup.mergedGroups.map(wizGroup => {
            return {id: wizGroup.id, groupName: wizGroup.groupName}
          });
        }
      }
      if (newWizObject.wizardSelectedValues[stepIndex].selectedFeature) {
        wizValues[stepIndex].selectedFeature = getCompressedFeature(
          null,
          newWizObject.wizardSelectedValues[stepIndex].selectedFeature,
          newWizObject.quoteIndex
        ); // to keep clean in json
      }
      if (newWizObject.wizardSelectedValues[stepIndex].quantityItems && newWizObject.wizardSelectedValues[stepIndex].quantityItems.length) {
        wizValues[stepIndex].quantityItems = newWizObject.wizardSelectedValues[stepIndex].quantityItems.map(quanItem => {
          return {
            quanFeature: getCompressedFeature(null, quanItem.quanFeature),
            quantity: quanItem.quantity,
          }
        })
      }
    })
    return Object.assign({}, newWizObject, {wizardSelectedValues: wizValues})
  });
  // console.log("allWizardsValues to save ->", allWizardsValuesToSave);
  return allWizardsValuesToSave
}

const getPrimaryStepGroup = (step) => step?.stepGroups?.[0] ?? null;

const isBlockingGroup = (g) => {
  if (!g) return true;
  if (g.mergedGroups?.length) return true;   // hard gate
  if (!g.notRequired) return true;           // required gate
  return false;
};

/**
 * checks if the following step is disabled
 * @returns {boolean}
 * @param props
 */
export const getWizardStepIsDisabled = (props) => {
  const {stepIndex, wizardSelectedValues, wizardSteps, sqftModalFixState, wizardConflictPopupOpened, wizardConflictingSteps} = props;
  const isIncompatibleModalOpened = sqftModalFixState.isModalFixOpen && sqftModalFixState.id === 'incompatibleCartItems';
  const isIncompatibleWizardGroupExist = wizardConflictingSteps?.some((wizardConflictingStep) => {
    return stepIndex === wizardConflictingStep.stepIndex;
  })

  if ((isIncompatibleModalOpened || (wizardConflictPopupOpened && wizardConflictingSteps?.length)) && !isIncompatibleWizardGroupExist) {
    return true
  }

  if (stepIndex === 0) return false;

  // Find the closest "blocking" step before stepIndex
  for (let i = stepIndex - 1; i >= 0; i--) {
    const selected = wizardSelectedValues[i];
    const group = selected?.wizardGroup || getPrimaryStepGroup(wizardSteps[i]);

    if (!group) return true;

    if (isBlockingGroup(group)) {
      // if the closest blocking step isn't selected -> disable
      return !selected?.wizardGroup;
    }
    // non-blocking (notRequired without mergedGroups) -> keep scanning back
  }

  return false;
}

export const getWizardProductGroupName = (wizardGroup) => {
  return wizardGroup.productCartTitle || wizardGroup.title || wizardGroup.groupName || ""
}

/**
 * checks if we need to request sharedFeatureNames from backend instead of the regular request
 * @param wizardGroup {wizardGroupInterface}
 * @param featureGroup {featureGroupInterface}
 * @returns {boolean}
 */
export const shouldUseNewWizardFeaturesRequestsLogic = (wizardGroup, featureGroup) => {
  if (!wizardGroup || !wizardGroup.useIncluded || !wizardGroup.featuresIncluded || !wizardGroup.featuresIncluded.length
    || !featureGroup) {
    return false
  }
  if (!featureGroup.getRealFeaturesReceivedProperty()) {
    return true
  }
  return featureGroup.counts && Object.keys(featureGroup.counts).some(layoutType => {
    // if less - we will request features with regular request cause it is faster
    // we are checking in all layouts case they might be not uploaded(need to change after feature overhaul)
    return featureGroup.counts[layoutType] > 1000
  })
}


/**
 * checks if the following step is disabled
 * @param currentNavItems {[navItemInterface]}
 * @param wizardGroup {wizardGroupInterface}
 * @param featureGroup {featureGroupInterface}
 * @returns {boolean}
 */
export const getMissingWizardFeatures = async (currentNavItems,
                                               wizardGroup,
                                               featureGroup) => {

  if (featureGroup.requestInProgress) {
    // we will request features with regular request cause it is faster
    return false
  }

  const {wizardMissingFeatures, wizardActiveItem} = store.getState().wizardState;

  const missingKey = getWizardMissingKey(wizardActiveItem, wizardGroup);
  console.log("missingKey", missingKey, wizardMissingFeatures);

  if (wizardMissingFeatures[missingKey] && !wizardMissingFeatures[missingKey].length) {
    // already received everything
    return false
  }

  const missingSkus = (wizardGroup.featuresIncluded || [])
    .filter(feature => {
      if (currentNavItems.some(navItem => navItem.part.getLowerCasedSharedFeatureName() === feature.sharedFeatureName)) {
        return false
      }
      // we already know missing sku's and we don't want to request the ones that we requested already
      if (wizardMissingFeatures[missingKey] && wizardMissingFeatures[missingKey].length
        && !wizardMissingFeatures[missingKey].includes(feature.sharedFeatureName)) {
        return false
      }
      // we already know missing sku's and we don't want to request the ones that we requested already end
      return true
    })
    .map(feature => feature.sharedFeatureName); // convert to names only array

  const devMode = Config.isDevMode();
  if (devMode) {
    console.log("current missing skus", missingSkus);
  }

  if (!missingSkus.length) { // first time we create it with requested features // small chances but let it be
    store.dispatch(setWizardMissingFeatures(missingKey, missingSkus));
    return
  }

  const slicedArray = missingSkus.slice(0, 100); // take first 100 features
  const stillMissingArray = missingSkus.slice(100); // from 100 to the end

  // 2) get missing features from the backend;
  if (devMode) {
    console.log("request more missingSkus", slicedArray);
    console.log("stillMissingArray", stillMissingArray);
  }
  // loaderEnable();
  featureGroup.requestInProgress = true;
  // const missingSkusResponse = await getBvFeatures(null, slicedArray, true);
  let getOnlyGroupNames = [];
  if (featureGroup.nextFeaturesPageToLoadArray) {
    if (COUNTERTOP_GROUP.includes(featureGroup.partId)) {
      getOnlyGroupNames = ["Countertop", "Backsplash", "Center Island Countertop"]
    } else if (COUNTERTOP_GROUP_BATHROOM.includes(featureGroup.partId)) {
      getOnlyGroupNames = ["Countertop", "Backsplash"]
    } else if (CABINET_GROUP.includes(featureGroup.partId)) {
      getOnlyGroupNames = ["Top Cabinet", "Bottom Cabinet", "Center Island Cabinet", "Fridge Enclosure", "Tall Cabinet"]
    }
  }
  const missingSkusResponse = getOnlyGroupNames.length
    ? await getBvFeatures(null, slicedArray, true, null, "", null, getOnlyGroupNames)
    : await getBvFeatures(null, slicedArray, true)
  // console.log("notReturnedFeatures", slicedArray.filter(name => !missingSkusResponse.data.some(f => (f.sharedFeatureName || "").toLowerCase() === name)));
  featureGroup.requestInProgress = false;

  store.dispatch(setWizardMissingFeatures(missingKey, stillMissingArray)); // set features that left

  if (missingSkusResponse && missingSkusResponse.data) {
    if (devMode) {
      console.log("missingSkusResponse", missingSkusResponse.data);
    }
    const grouped = new Map();
    for (const item of missingSkusResponse.data) {
      if (!grouped.has(item.groupName)) {
        grouped.set(item.groupName, { groupName: item.groupName, features: [] });
      }
      grouped.get(item.groupName).features.push(item);
    }
    const byGroupsResponse = Array.from(grouped.values());
    if (devMode) {
      console.log("byGroupsResponse", byGroupsResponse);
    }
    const layout = getCurrentLayout();
    byGroupsResponse.forEach(groupObject => {
      const featureGroup = layout.getFeatureGroupByGroupName(groupObject.groupName);
      layout.loadNewFeatures(groupObject.features, featureGroup);
    })
    return true
  }

  // loaderDisable();
  return false
}

/**
 * returns something like "1-18"
 * @param wizardItem {wizardItemInterface}
 * @param wizardGroup {wizardGroupInterface}
 * @returns {string}
 */
export const getWizardMissingKey = (wizardItem, wizardGroup) => {
  if (!wizardItem || !wizardGroup) {
    return ""
  }
  if (wizardGroup.mergedGroups && wizardGroup.mergedGroups[0]) {
    return wizardItem.id + "-" + wizardGroup.mergedGroups[0].id
  }
  return wizardItem.id + "-" + wizardGroup.id
}

/**
 * this hook is disabling feature groups for hide items wizards because they should not be added to the cart
 * @param wizardActiveItem {wizardItemInterface}
 * @param visualizersLoadedLength {number}
 * @param wizardSelectedValues {object}
 * @param updateLookCartItem {function}
 * @returns {boolean}
 */
export const useHideItemsDisabledfeatureGroups = (wizardActiveItem, visualizersLoadedLength, wizardSelectedValues, updateLookCartItem = null) => {
  useEffect(() => {
    if (wizardActiveItem && wizardActiveItem.hideItems && visualizersLoadedLength) {
      wizardActiveItem.wizardGroups.forEach(wizardGroup => {
        if (Object.keys(wizardSelectedValues).some(key => wizardSelectedValues[key].wizardGroup.groupName === wizardGroup.groupName)) {
          return // do not disable already selected in wizard groups (restoring logic)
        }
        const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
        if (featureGroup) {
          featureGroup.disabled = true;
          if (featureGroup.layerPositions) {
            Object.keys(featureGroup.layerPositions).forEach(position => {
              featureGroup.layerPositions[position] = null;
            })
          }
          console.log("featureGroup disabled", featureGroup);
        }
      })
    }
    if (updateLookCartItem) {
      updateLookCartItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizersLoadedLength]);
}

export const getNextWizardStepQuantity = (currentGroups, wizardSelectedValues, wizardActiveStep, fromFeature, wizardSqftCartItems = []) => {
  let newQuantity = 1
  if (currentGroups.length === 1) {
    const wizardGroup = currentGroups[0];
    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
    if (wizardSelectedValues[wizardActiveStep] && Number.isInteger(wizardSelectedValues[wizardActiveStep].quantity)) {
      newQuantity = wizardSelectedValues[wizardActiveStep].quantity;
    } else if (wizardSqftCartItems.length && (fromFeature || featureGroup)) {
      const groupName = fromFeature ? fromFeature.dbFeature.groupName : (featureGroup.dbName || featureGroup.partName);
      const addedCartItems = wizardSqftCartItems.filter(cItem => cItem.cartFeature && cItem.cartFeature.dbFeature.groupName === groupName);
      // console.log("addedCartItems", addedCartItems);
      if (addedCartItems.length) {
        newQuantity = addedCartItems.reduce((total, cItem) => total + cItem.quantity, 0);
      } else {
        newQuantity = getFeatureGroupFeaturesQuantity(fromFeature, featureGroup, wizardGroup);
      }
    } else {
      newQuantity = getFeatureGroupFeaturesQuantity(fromFeature, featureGroup, wizardGroup);
    }
  }
  return newQuantity
}

/**
 * this returns on qkb wizard custom groups or feature groups as features
 * @param currentGroups {[wizardGroupInterface]}
 * @returns {boolean}
 */
export const displayMaterialsWizardGroupAsFeature = (currentGroups) => {
  return currentGroups.length > 1 || (currentGroups.length === 1 && currentGroups[0].nodeType === WIZARD_GROUP_TYPES.custom)
}

/**
 * this returns on qkb wizard wizardGroup from current steps
 * @param wizardItem {wizardItemInterface}
 * @param wizardValues {object}
 * @param neededWizardGroupId {string}
 * @returns {object}
 */
export const getWizardGroupFromCurrentSteps = (wizardItem, wizardValues, neededWizardGroupId) => {
  let neededStep, neededWizardGroup;
  if (!wizardItem || !wizardValues || !neededWizardGroupId) {
    return {neededStep, neededWizardGroup}
  }
  getAvailableSteps(wizardItem, wizardValues, true).forEach(step => {
    if (neededStep) {
      return
    }
    step.stepGroups.forEach(wizGroup => {
      if (wizGroup.id === neededWizardGroupId) {
        neededStep = step;
        neededWizardGroup = wizGroup;
      } else if (wizGroup.mergedGroups) {
        wizGroup.mergedGroups.forEach(mergedGroup => {
          if (mergedGroup.id === neededWizardGroupId) {
            neededStep = step;
            neededWizardGroup = mergedGroup;
          }
        })
      }
    })
  });
  return {neededStep, neededWizardGroup}
}
